import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { createPortal } from 'react-dom';
import { EditBtn, EditBtnIcon } from '~/editor/buttons/EditBtn';
import { useEditorStore, EditorMediaFile } from '~/shell/Store';
import { useLocation } from 'react-router-dom';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import type { MediaFile } from '~/editor/filePicker/components/MediaGrid';

// Reuse MediaFile shape from file picker

// Styled wrappers that fully disable animations/transitions regardless of upstream styles
const NoAnimEditBtn = styled(EditBtn)`
  &,
  & * {
    transition: none !important;
    animation: none !important;
    animation-name: none !important;
    animation-duration: 0s !important;
    animation-delay: 0s !important;
    animation-iteration-count: 0 !important;
    transform: none !important;
  }
  &::before,
  &::after {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
  &:hover,
  &:active,
  &:focus {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
`;

const NoAnimEditBtnIcon = styled(EditBtnIcon)`
  &,
  & * {
    transition: none !important;
    animation: none !important;
    animation-name: none !important;
    animation-duration: 0s !important;
    animation-delay: 0s !important;
    animation-iteration-count: 0 !important;
    transform: none !important;
  }
  &::before,
  &::after {
    transition: none !important;
    animation: none !important;
    transform: none !important;
  }
`;

export const EditBg = ({
  divElRef,
  id,
  type,
  style,
  cb
}: {
  divElRef: React.RefObject<HTMLDivElement>;
  id: string;
  type: string;
  style?: React.CSSProperties;
  cb?: (body: { id: string; file: unknown; type: string }) => void;
}) => {
  const fileElRef = useRef<HTMLDivElement>(null);
  const { updateFile } = useEditorStore();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showFileUpload = queryParams.get('showFileUpload') === 'true';

  //it is for learner journey
  const [showFilePicker, setShowFilePicker] = useState(
    showFileUpload && type === 'video'
  );

  const [compStyle, setCompStyle] = useState({
    position: 'absolute',
    top: '80px',
    zIndex: 199,
    width: '28px',
    right: '',
    ...style
  });

  const updatePosition = useCallback(() => {
    if (divElRef.current) {
      const pos = divElRef.current.getBoundingClientRect();

      // Default to top-left with a margin
      let left = 20;
      let top = pos?.y ? 20 : 80;
      let useRightAnchor = false; // we want to anchor on the left side now

      // Special handling for images that use object-fit: contain
      if (type === 'image') {
        const imgEl = divElRef.current.querySelector(
          'img'
        ) as HTMLImageElement | null;

        const cw = divElRef.current.clientWidth;
        const ch = divElRef.current.clientHeight;

        const iw = imgEl?.naturalWidth || 0;
        const ih = imgEl?.naturalHeight || 0;

        if (imgEl && iw > 0 && ih > 0 && cw > 0 && ch > 0) {
          // Compute the rendered (contained) size
          const scale = Math.min(cw / iw, ch / ih);
          const dw = iw * scale;
          const dh = ih * scale;
          const offsetX = (cw - dw) / 2;
          const offsetY = (ch - dh) / 2;

          // Place the button inside the top-left corner of the rendered image box
          const margin = 20; // visual padding inside
          left = Math.max(0, offsetX + margin);
          top = Math.max(0, offsetY + margin);
          useRightAnchor = false;
        } else {
          // Fallback to top-left when image metrics are not ready
          left = 20;
          top = 20;
        }
      } else {
        // Non-image: keep a consistent top-left margin
        left = 20;
        top = pos?.y ? 20 : 80;
      }

      setCompStyle(prevStyle => ({
        ...prevStyle,
        left: `${left}px`,
        right: useRightAnchor ? '20px' : '',
        top: `${top}px`
      }));
    }
  }, [divElRef, type]);

  const UpdateFileElement = (file: EditorMediaFile) => {
    if (cb) {
      cb({ id: id, file: file, type: type == 'image' ? 'pictures' : 'videos' });
    } else {
      updateFile(id, file, type == 'image' ? 'pictures' : 'videos');
    }
    setShowFilePicker(false);
  };

  useEffect(() => {
    updatePosition();

    if (divElRef?.current) {
      window.addEventListener('resize', updatePosition);
      updatePosition();

      const resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });

      resizeObserver.observe(divElRef.current);

      // Recalculate when the image loads (important for naturalWidth/Height)
      let imgEl: HTMLImageElement | null = null;
      if (type === 'image') {
        imgEl = divElRef.current.querySelector('img');
        if (imgEl) {
          imgEl.addEventListener('load', updatePosition);
        }
      }

      return () => {
        window.removeEventListener('resize', updatePosition);
        resizeObserver.disconnect();
        if (imgEl) {
          imgEl.removeEventListener('load', updatePosition);
        }
      };
    }
  }, [divElRef, type, updatePosition]);

  return (
    <>
      <NoAnimEditBtn
        onMouseUp={e => {
          e.preventDefault();
          e.stopPropagation();
          setShowFilePicker(!showFilePicker);
        }}
        style={compStyle as React.CSSProperties}
        className="edit-bg-btn"
      >
        <NoAnimEditBtnIcon />
      </NoAnimEditBtn>
      {showFilePicker &&
        createPortal(
          <FilePickerV2
            fileElRef={fileElRef}
            updateFile={UpdateFileElement}
            type={type as 'image' | 'video' | 'document'}
            onClose={() => setShowFilePicker(false)}
          />,
          document.body
        )}
    </>
  );
};
